import { Popover } from 'antd';
import React, { ReactNode } from 'react';

import SvgIcon from '~src/shared/SvgIcon';

import {
  HoverBtnBox,
  HoverContainer,
  HoverText,
  InfoBox,
  ListItemContainer,
  MainContent,
  PropsBox,
  SingleItem,
  Title,
  TitleBlock,
  ToolTipContent,
} from './listItem.sc';
import { HoverBtnSettings, HoverContent, InternalUrl, ListItemProps } from './types';

interface Props extends DefProps {
  title: string | ReactNode;
  itemProps?: ListItemProps<string|ReactNode>;
  hoverContent?: HoverContent;
  content?: ReactNode;
}

interface DefProps {
  titleContent?: ReactNode;
  rightSection?: ReactNode;
}

const renderInnerButton = (btn: HoverBtnSettings, isLast?: boolean): ReactNode => {
  const icon = <SvgIcon name={btn.icon || 'iconGear'} />;
  if (btn.hoverTxt) {
    return (
      <Popover
        arrowPointAtCenter
        placement={isLast ? 'bottomRight' : 'bottom'}
        content={<ToolTipContent>{btn.hoverTxt}</ToolTipContent>}
        trigger="hover"
      >
        <div>{icon}</div>
      </Popover>
    );
  } else {
    return (
      <>
        <HoverText data-translation={btn.key}>{btn.txt}</HoverText>
        {icon}
      </>
    );
  }
};

const internalUrlToLocation = (url: string | InternalUrl) => {
  if (typeof url === 'string') {
    return { pathname: url };
  }

  return {
    pathname: url.path,
    search: Object.keys(url.search)
      .filter((key) => !!url.search[key])
      .map((key) => `${key}=${url.search[key]}`)
      .join('&'),
  };
};

const renderHoverBox = ({ wideBtn, smallBtns, background, color }: HoverContent): ReactNode => (
  <HoverContainer $bg={background}>
    {wideBtn && (
      <HoverBtnBox
        onClick={wideBtn.click ? wideBtn.click : undefined}
        $color={color}
        $hoverColor={wideBtn.hoverColor}
        className="wide-btn"
        to={{
          ...internalUrlToLocation(wideBtn.link || ''),
          state: wideBtn.locationState,
        }}
      >
        {smallBtns && smallBtns.length > 0 ? (
          renderInnerButton(wideBtn)
        ) : (
          <SingleItem>
            <span>{wideBtn.hoverTxt || wideBtn.txt}</span>
            <SvgIcon name={wideBtn.icon || 'iconGear'} />
          </SingleItem>
        )}
      </HoverBtnBox>
    )}
    {smallBtns &&
      smallBtns.map((smallBtn, i) => (
        <HoverBtnBox
          key={smallBtn.key}
          onClick={smallBtn.click}
          $color={color}
          $hoverColor={smallBtn.hoverColor}
          className={smallBtn.hoverTxt ? 'small-btn' : ''}
          to={
            typeof smallBtn.link === 'string'
              ? { pathname: smallBtn.link, state: smallBtn.locationState }
              : { ...smallBtn.link, state: smallBtn.locationState }
          }
        >
          {renderInnerButton(smallBtn, smallBtns.length === ++i)}
        </HoverBtnBox>
      ))}
  </HoverContainer>
);

const ListItemPropsDisplay: React.FC<{ itemProps: ListItemProps<string|ReactNode> }> = React.memo(
  function ListItemPropsDisplay({ itemProps }) {
    return (
      <PropsBox>
        {itemProps.map((prop, i) => (
          <span key={i} data-translation={prop.key}>
            {prop.txt}
          </span>
        ))}
      </PropsBox>
    );
  },
);

const ListItem: React.FC<Props> = React.memo(function ListItem({
  title,
  itemProps,
  hoverContent,
  titleContent = null,
  rightSection = null,
  content,
}) {
  return (
    <ListItemContainer>
      <MainContent>
        <TitleBlock>
          <Title>{title}</Title>
          {titleContent}
        </TitleBlock>
        <InfoBox>{itemProps && <ListItemPropsDisplay itemProps={itemProps} />}</InfoBox>
        {content}
      </MainContent>
      {rightSection}
      {hoverContent && renderHoverBox(hoverContent)}
    </ListItemContainer>
  );
});

export { ListItemPropsDisplay, ListItem as default };
